import { ShoppingBagIcon } from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { EmptyTabState } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';

import { cn, useDisclosure } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

import useBulkOrdering from '../hooks/useBulkOrdering';
import OrderDetailModal from './OrderDetailModal';
import OrdersTable from './OrdersTable';

interface ProductDetailOrdersTabProps {
  bulkOrderings: BulkOrdering[];
  isLoading: boolean;
}
const ProductDetailOrdersTab: React.FC<ProductDetailOrdersTabProps> = ({
  bulkOrderings,
  isLoading
}) => {
  const { id: productId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const orderId = searchParams.get('order');
  const { data: order, isLoading: isOrderLoading } = useBulkOrdering(
    orderId || undefined
  );

  useEffect(() => {
    if (orderId) {
      onOpen();
    }
  }, [orderId, onOpen]);

  const removeSearchParams = () => {
    setSearchParams({ tab: 'orders' });
  };

  const hasNoOrders = !isLoading && !bulkOrderings?.length;

  return (
    // The empty state centers itself against a full-height flex parent, the
    // same way the Updates tab does; the table layout keeps its natural height.
    <div className={cn(hasNoOrders && 'flex flex-col h-full')}>
      {hasNoOrders ? (
        <EmptyTabState
          icon={ShoppingBagIcon}
          title="No orders"
          description="Your orders will appear here after being placed"
          action={
            <Link
              to={`${ROUTE_PATHS.PRODUCTS}/${productId}?tab=quotes`}
              className="text-primary underline text-sm font-medium"
            >
              Go to quotes
            </Link>
          }
        />
      ) : (
        <>
          <Typography variant="h4" as="h2" weight="bold">
            {bulkOrderings?.length}{' '}
            {bulkOrderings?.length === 1 ? 'Order' : 'Orders'}
          </Typography>

          <OrdersTable
            orders={bulkOrderings ?? []}
            isLoading={isLoading}
            isProductTab
          />
        </>
      )}

      <OrderDetailModal
        order={order ?? null}
        isOpen={isOpen}
        isLoading={isOrderLoading}
        onClose={onOpenChange}
        onRemoveSearchParams={removeSearchParams}
      />
    </div>
  );
};

export default ProductDetailOrdersTab;
